import React, { useMemo } from 'react';
import { Footer, Arrow, LinkButton } from '../HBCalloutPopover.styles';
import {
    IBasicAction,
    THBCalloutPopoverTheme
} from '../HBCalloutPopover.types';
import HBPopoverActions from '../../HBPopover/HBPopoverActions';
import HBText from '../../HBText/HBText';
import styled, { css } from 'styled-components';

export interface ICalloutPopoverActionsProps {
    styles: THBCalloutPopoverTheme;
    primaryActionType?: 'button' | 'link-button';
    actions?: [IBasicAction] | [IBasicAction, IBasicAction];
    actionsOrientation?: 'horizontal' | 'vertical';
}

const getActionProperties = (
    { text, onClick, disabled }: IBasicAction,
    testIdName: string
) => {
    return {
        text,
        onClick,
        disabled,
        testIdName
    };
};

const HBCalloutPopoverActionsAndPagination = (
    props: ICalloutPopoverActionsProps & {
        primaryActionTestId: string;
        secondaryActionTestId?: string;
        stepsContent?: { currStep: number; totalSteps: number };
    }
) => {
    const {
        styles,
        primaryActionType = 'button',
        actions,
        primaryActionTestId,
        secondaryActionTestId,
        actionsOrientation,
        stepsContent
    } = props;

    const showLinkButton =
        primaryActionType === 'link-button' && actions.length > 0;

    const popoverActions = useMemo(() => {
        if (showLinkButton || !actions?.length) {
            return [];
        }

        const _actions = [
            {
                ...getActionProperties(actions[0], primaryActionTestId),
                theme: styles.primaryActionTheme
            }
        ];

        if (actions[1]) {
            _actions.unshift({
                ...getActionProperties(actions[1], secondaryActionTestId),
                theme: styles.secondaryActionTheme
            });
        }

        return _actions;
    }, [
        actions,
        primaryActionTestId,
        secondaryActionTestId,
        showLinkButton,
        styles.primaryActionTheme,
        styles.secondaryActionTheme
    ]);

    if (!actions?.length) {
        return null;
    }

    const paginationContent = (
        <HBText type="subtitle12" color={styles.paginationColor}>
            {`${stepsContent?.currStep} OF ${stepsContent?.totalSteps}`}
        </HBText>
    );

    return showLinkButton ? (
        <Footer>
            <LinkButton
                linkButtonTheme={styles.LinkButtonTheme}
                onClick={actions[0].onClick}
                disabled={actions[0].disabled}
                testId={primaryActionTestId}
                iconPosition="end"
                icon={<Arrow color={styles.iconColor} />}
                size="sm"
            >
                {actions[0].text}
            </LinkButton>
        </Footer>
    ) : (
        <ActionsAndPaginationWrapper $hasSteps={!!stepsContent}>
            {!!stepsContent && paginationContent}
            <HBPopoverActions
                actions={popoverActions}
                actionsOrientation={actionsOrientation}
                actionsAlignment="right"
            />
        </ActionsAndPaginationWrapper>
    );
};

export default HBCalloutPopoverActionsAndPagination;

const ActionsAndPaginationWrapper = styled.div<{ $hasSteps: boolean }>`
    ${({ $hasSteps }) =>
        $hasSteps &&
        css`
            display: flex;
            justify-content: space-between;
            align-items: center;
        `}
`;
